<script setup>
import FacebookIcon from '@iconify-vue/logos/facebook';
import GoogleAdsIcon from '@iconify-vue/logos/google-ads';
import GoogleAnalyticsIcon from '@iconify-vue/logos/google-analytics';
import GoogleSearchConsoleIcon from '@iconify-vue/logos/google-search-console';
import InstagramIcon from '@iconify-vue/logos/instagram-icon';
import MailjetIcon from '@iconify-vue/logos/mailjet-icon';
import MetaIcon from '@iconify-vue/logos/meta-icon';
import GearFineIcon from '@iconify-vue/ph/gear-fine-fill';
import StorefrontIcon from '@iconify-vue/ph/storefront-fill';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import AmazonIcon from '@js/components/icons/colored/AmazonIcon.vue';
import GoogleMerchantCenterIcon from '@js/components/icons/colored/GoogleMerchantCenterIcon.vue';
import GoogleMyBusinessIcon from '@js/components/icons/colored/GoogleMyBusinessIcon.vue';
import GuaranteedReviewsCompanyIcon from '@js/components/icons/colored/GuaranteedReviewsCompanyIcon.vue';
import HiboutikIcon from '@js/components/icons/colored/HiboutikIcon.vue';
import ManoManoIcon from '@js/components/icons/colored/ManoManoIcon.vue';
import PrintNodeIcon from '@js/components/icons/colored/PrintNodeIcon.vue';
import VerifiedReviewsIcon from '@js/components/icons/colored/VerifiedReviewsIcon.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { archived = false } = defineProps({
    archived: {
        type: Boolean,
        default: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'eCommerce',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'E-commerce',
                    icon: StorefrontIcon,
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                type: props.row.original.eCommerce
                                    ? 'success'
                                    : 'secondary',
                                icon: GearFineIcon,
                                size: 'small',
                                square: true,
                                href: route(
                                    'front.projectManagement.customer.externalServices.eCommerce',
                                    { tenant: props.row.original.id },
                                ),
                            }),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'googleAds',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Google Ads',
                    icon: GoogleAdsIcon,
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                type: props.row.original.googleAds
                                    ? 'success'
                                    : 'secondary',
                                icon: GearFineIcon,
                                size: 'small',
                                square: true,
                                href: route(
                                    'front.projectManagement.customer.externalServices.googleAds',
                                    { tenant: props.row.original.id },
                                ),
                            }),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'googleMerchant',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Google Merchant',
                    icon: GoogleMerchantCenterIcon,
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                type: props.row.original.googleMerchant
                                    ? 'success'
                                    : 'secondary',
                                icon: GearFineIcon,
                                size: 'small',
                                square: true,
                                href: route(
                                    'front.projectManagement.customer.externalServices.googleMerchant',
                                    { tenant: props.row.original.id },
                                ),
                            }),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'googleSearchConsole',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Google Search Console',
                    icon: GoogleSearchConsoleIcon,
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                type: props.row.original.googleSearchConsole
                                    ? 'success'
                                    : 'secondary',
                                icon: GearFineIcon,
                                size: 'small',
                                square: true,
                                href: route(
                                    'front.projectManagement.customer.externalServices.googleSearchConsole',
                                    { tenant: props.row.original.id },
                                ),
                            }),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'googleAnalytics',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Google Analytics',
                    icon: GoogleAnalyticsIcon,
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                type: props.row.original.googleAnalytics
                                    ? 'success'
                                    : 'secondary',
                                icon: GearFineIcon,
                                size: 'small',
                                square: true,
                                href: route(
                                    'front.projectManagement.customer.externalServices.googleAnalytics',
                                    { tenant: props.row.original.id },
                                ),
                            }),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'googleMyBusiness',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Google My Business',
                    icon: GoogleMyBusinessIcon,
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                type: props.row.original.googleMyBusiness
                                    ? 'success'
                                    : 'secondary',
                                icon: GearFineIcon,
                                size: 'small',
                                square: true,
                                href: route(
                                    'front.projectManagement.customer.externalServices.googleMyBusiness',
                                    { tenant: props.row.original.id },
                                ),
                            }),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'facebookAds',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Meta',
                    icon: MetaIcon,
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                type: props.row.original.facebookAds
                                    ? 'success'
                                    : 'secondary',
                                icon: GearFineIcon,
                                size: 'small',
                                square: true,
                                href: route(
                                    'front.projectManagement.customer.externalServices.facebookAds',
                                    { tenant: props.row.original.id },
                                ),
                            }),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'facebook',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Facebook',
                    icon: FacebookIcon,
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                type: props.row.original.facebook
                                    ? 'success'
                                    : 'secondary',
                                icon: GearFineIcon,
                                size: 'small',
                                square: true,
                                href: route(
                                    'front.projectManagement.customer.externalServices.facebook',
                                    { tenant: props.row.original.id },
                                ),
                            }),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'instagram',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Instagram',
                    icon: InstagramIcon,
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                type: props.row.original.facebookAds
                                    ? 'success'
                                    : 'secondary',
                                icon: GearFineIcon,
                                size: 'small',
                                square: true,
                                href: route(
                                    'front.projectManagement.customer.externalServices.instagram',
                                    { tenant: props.row.original.id },
                                ),
                            }),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'printNode',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'PrintNode',
                    icon: PrintNodeIcon,
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                type: props.row.original.printNode
                                    ? 'success'
                                    : 'secondary',
                                icon: GearFineIcon,
                                size: 'small',
                                square: true,
                                href: route(
                                    'front.projectManagement.customer.externalServices.printNode',
                                    { tenant: props.row.original.id },
                                ),
                            }),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'mailjet',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Mailjet',
                    icon: MailjetIcon,
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                type: props.row.original.mailjet
                                    ? 'success'
                                    : 'secondary',
                                icon: GearFineIcon,
                                size: 'small',
                                square: true,
                                href: route(
                                    'front.projectManagement.customer.externalServices.mailjet',
                                    { tenant: props.row.original.id },
                                ),
                            }),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'guaranteedReviewsCompany',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Société des Avis Garantis',
                    icon: GuaranteedReviewsCompanyIcon,
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                type: props.row.original
                                    .guaranteedReviewsCompany
                                    ? 'success'
                                    : 'secondary',
                                icon: GearFineIcon,
                                size: 'small',
                                square: true,
                                href: route(
                                    'front.projectManagement.customer.externalServices.guaranteedReviewsCompany',
                                    { tenant: props.row.original.id },
                                ),
                            }),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'verifiedReviews',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Avis Vérifiés',
                    icon: VerifiedReviewsIcon,
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                type: props.row.original.verifiedReviews
                                    ? 'success'
                                    : 'secondary',
                                icon: GearFineIcon,
                                size: 'small',
                                square: true,
                                href: route(
                                    'front.projectManagement.customer.externalServices.verifiedReviews',
                                    { tenant: props.row.original.id },
                                ),
                            }),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'hiboutik',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Hiboutik',
                    icon: HiboutikIcon,
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                type: props.row.original.hiboutik
                                    ? 'success'
                                    : 'secondary',
                                icon: GearFineIcon,
                                size: 'small',
                                square: true,
                                href: route(
                                    'front.projectManagement.customer.externalServices.hiboutik',
                                    { tenant: props.row.original.id },
                                ),
                            }),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'amazon',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Amazon',
                    icon: AmazonIcon,
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                type: props.row.original.amazon
                                    ? 'success'
                                    : 'secondary',
                                icon: GearFineIcon,
                                size: 'small',
                                square: true,
                                href: route(
                                    'front.projectManagement.customer.externalServices.amazon',
                                    { tenant: props.row.original.id },
                                ),
                            }),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'manoMano',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ManoMano',
                    icon: ManoManoIcon,
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                type: props.row.original.manoMano
                                    ? 'success'
                                    : 'secondary',
                                icon: GearFineIcon,
                                size: 'small',
                                square: true,
                                href: route(
                                    'front.projectManagement.customer.externalServices.manoMano',
                                    { tenant: props.row.original.id },
                                ),
                            }),
                        ],
                    },
                ),
        }),
    ],
    url: route(
        'front.projectManagement.customer.postExternalServiceConfigurations',
    ),
    data: {
        archived,
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    />
</template>
